import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getISOWeekKey, weekKeyToLabel, weekKeyToDateRange } from "@/lib/week";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Sparkles,
  Heart,
  ClipboardList,
  Cloud,
  Compass,
  Save,
  History as HistoryIcon,
  ImagePlus,
  MailPlus,
  CheckCircle2,
} from "lucide-react";

const EMPTY = {
  reflect_well: "",
  reflect_challenged: "",
  reflect_proud: "",
  prepare_appointments: "",
  prepare_deadlines: "",
  prepare_bills: "",
  prepare_meals: "",
  prepare_laundry: "",
  plan_priorities: ["", "", ""],
  plan_habits: "",
  plan_excited: "",
};

const QUOTE = "Reflect. Prepare. Dream. Plan.";

export default function SundayResetCard({ weekKey: weekKeyProp, showHistoryLink = true, variant = "default" }) {
  const weekKey = weekKeyProp || getISOWeekKey(new Date());
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: doc } = await api.get(`/sunday-reset/${weekKey}`);
      if (doc) {
        setData({
          ...EMPTY,
          ...doc,
          plan_priorities: Array.isArray(doc.plan_priorities) && doc.plan_priorities.length
            ? [doc.plan_priorities[0] || "", doc.plan_priorities[1] || "", doc.plan_priorities[2] || ""]
            : ["", "", ""],
        });
        if (doc.updated_at) setSavedAt(new Date(doc.updated_at));
      } else {
        setData(EMPTY);
        setSavedAt(null);
      }
    } catch (e) {
      // 404 means no entry yet — keep EMPTY
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [weekKey]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = useCallback((key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  }, []);
  const updatePriority = useCallback((i, value) => {
    setData((prev) => {
      const next = [...(prev.plan_priorities || ["", "", ""])];
      next[i] = value;
      return { ...prev, plan_priorities: next };
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data, week_key: weekKey };
      const { data: doc } = await api.put(`/sunday-reset/${weekKey}`, payload);
      if (doc?.updated_at) setSavedAt(new Date(doc.updated_at));
      toast.success("Sunday Reset saved", { description: weekKeyToLabel(weekKey) });
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const wrapper =
    variant === "page"
      ? "rounded-3xl border-2 border-primary/30 bg-card p-6 sm:p-8 shadow-sm relative overflow-hidden"
      : "rounded-2xl border-2 border-primary/30 bg-card p-5 sm:p-6 shadow-sm relative overflow-hidden";

  return (
    <div
      className={wrapper}
      data-testid="sunday-reset-card"
      style={{
        backgroundImage:
          "radial-gradient(circle at 100% 0%, hsl(var(--primary) / 0.10), transparent 55%), radial-gradient(circle at 0% 100%, hsl(var(--primary) / 0.06), transparent 50%)",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="size-11 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
            <Sparkles strokeWidth={1.5} className="size-6 text-primary" />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
              Sunday reset
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
              Your weekly reset
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {weekKeyToLabel(weekKey)} · {weekKeyToDateRange(weekKey)}
            </p>
          </div>
        </div>
        {showHistoryLink && (
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl shrink-0 hidden sm:inline-flex"
            data-testid="sunday-reset-history-link"
          >
            <Link to="/sunday-reset/history">
              <HistoryIcon strokeWidth={1.5} className="size-4 mr-1.5" />
              History
            </Link>
          </Button>
        )}
      </div>

      {/* Motivational quote */}
      <blockquote
        className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4 sm:p-5 mb-6 font-display text-lg sm:text-xl italic leading-snug"
        data-testid="sunday-reset-quote"
      >
        &ldquo;{QUOTE}&rdquo;
      </blockquote>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading your reset…</p>
      ) : (
        <div className="space-y-6">
          {/* REFLECT */}
          <Section
            icon={<Heart strokeWidth={1.5} className="size-5 text-primary" />}
            eyebrow="Reflect"
            title="Look back with kindness"
          >
            <Field
              label="What went well this week?"
              testid="reset-reflect-well"
              value={data.reflect_well}
              onChange={(v) => updateField("reflect_well", v)}
              placeholder="Wins big or small — celebrate them all."
            />
            <Field
              label="What challenged you?"
              testid="reset-reflect-challenged"
              value={data.reflect_challenged}
              onChange={(v) => updateField("reflect_challenged", v)}
              placeholder="Name it without judging it."
            />
            <Field
              label="What are you proud of?"
              testid="reset-reflect-proud"
              value={data.reflect_proud}
              onChange={(v) => updateField("reflect_proud", v)}
              placeholder="One moment that made you feel like yourself."
            />
          </Section>

          {/* PREPARE */}
          <Section
            icon={<ClipboardList strokeWidth={1.5} className="size-5 text-primary" />}
            eyebrow="Prepare"
            title="Set the week up for ease"
          >
            <Field
              label="Upcoming appointments"
              testid="reset-prepare-appointments"
              value={data.prepare_appointments}
              onChange={(v) => updateField("prepare_appointments", v)}
              placeholder="Doctor, calls, coffee dates…"
              rows={2}
            />
            <Field
              label="Important deadlines"
              testid="reset-prepare-deadlines"
              value={data.prepare_deadlines}
              onChange={(v) => updateField("prepare_deadlines", v)}
              placeholder="Work, school, paperwork."
              rows={2}
            />
            <Field
              label="Bills due this week"
              testid="reset-prepare-bills"
              value={data.prepare_bills}
              onChange={(v) => updateField("prepare_bills", v)}
              placeholder="Rent, cards, subscriptions."
              rows={2}
            />
            <Field
              label="Meal ideas"
              testid="reset-prepare-meals"
              value={data.prepare_meals}
              onChange={(v) => updateField("prepare_meals", v)}
              placeholder="A few easy wins so future-you isn't hungry."
              rows={2}
            />
            <Field
              label="Laundry / cleaning reminders"
              testid="reset-prepare-laundry"
              value={data.prepare_laundry}
              onChange={(v) => updateField("prepare_laundry", v)}
              placeholder="Sheets, towels, bathroom day…"
              rows={2}
            />
          </Section>

          {/* DREAM */}
          <Section
            icon={<Cloud strokeWidth={1.5} className="size-5 text-primary" />}
            eyebrow="Dream"
            title="Tend to your future"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button asChild variant="outline" className="rounded-xl h-auto py-3 justify-start" data-testid="reset-dream-vision">
                <Link to="/vision">
                  <ImagePlus strokeWidth={1.5} className="size-4 mr-2" />
                  <span className="text-left">
                    <span className="block text-sm font-semibold">Add to your vision board</span>
                    <span className="block text-[11px] text-muted-foreground">Pin something that pulls you forward</span>
                  </span>
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl h-auto py-3 justify-start" data-testid="reset-dream-letter">
                <Link to="/letters">
                  <MailPlus strokeWidth={1.5} className="size-4 mr-2" />
                  <span className="text-left">
                    <span className="block text-sm font-semibold">Write a letter to Future You</span>
                    <span className="block text-[11px] text-muted-foreground">Seal it for a week, a month, a year</span>
                  </span>
                </Link>
              </Button>
            </div>
          </Section>

          {/* PLAN */}
          <Section
            icon={<Compass strokeWidth={1.5} className="size-5 text-primary" />}
            eyebrow="Plan"
            title="Choose your week, gently"
          >
            <div>
              <Label>Top 3 priorities for the week</Label>
              <div className="space-y-2 mt-1.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="size-7 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <Input
                      data-testid={`reset-priority-${i}`}
                      value={data.plan_priorities[i] || ""}
                      onChange={(e) => updatePriority(i, e.target.value)}
                      placeholder={i === 0 ? "The one that matters most" : i === 1 ? "Another meaningful one" : "And one more"}
                      className="rounded-xl"
                    />
                  </div>
                ))}
              </div>
            </div>
            <Field
              label="Habits you want to focus on"
              testid="reset-plan-habits"
              value={data.plan_habits}
              onChange={(v) => updateField("plan_habits", v)}
              placeholder="Move daily · 10 pages · phone away by 9pm"
              rows={2}
            />
            <Field
              label="One thing you're excited about"
              testid="reset-plan-excited"
              value={data.plan_excited}
              onChange={(v) => updateField("plan_excited", v)}
              placeholder="A tiny spark of joy to look forward to."
            />
          </Section>

          {/* Save */}
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              {savedAt ? (
                <>
                  <CheckCircle2 strokeWidth={1.5} className="size-3.5 text-primary" />
                  Saved {savedAt.toLocaleString()}
                </>
              ) : (
                <span>Nothing saved yet for this week.</span>
              )}
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl"
              data-testid="reset-save-button"
            >
              <Save strokeWidth={1.5} className="size-4 mr-1.5" />
              {saving ? "Saving…" : "Save Sunday Reset"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ icon, eyebrow, title, children }) {
  return (
    <Card className="rounded-2xl border border-border/60 bg-background/40">
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-xl bg-primary/10 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
              {eyebrow}
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight leading-tight">
              {title}
            </h3>
          </div>
        </div>
        <div className="space-y-3">{children}</div>
      </CardContent>
    </Card>
  );
}

function Label({ children }) {
  return (
    <div className="text-sm font-semibold text-foreground/90">{children}</div>
  );
}

function Field({ label, testid, value, onChange, placeholder, rows = 2 }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testid}
        className="resize-y text-sm mt-1.5"
      />
    </div>
  );
}
